import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Button as GatsbyButton } from "gatsby-theme-material-ui"
import { CookieBanner } from "../components/cookieBanner"
import {
  Container,
  Grid,
  Box,
  Link,
  Typography,
  ListItem,
  List,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { getGatsbyImageData } from "gatsby-source-sanity"
import { SocialIcon } from "react-social-icons"
import { Newsletter } from "./newsletter"
import { prefixLink } from "../utils/linkHelpers"

export const Footer = props => {
  const { previewData, sanityConfig, showNewsletter = true } = props
  const theme = useTheme()
  const data = useStaticQuery(graphql`
    query FooterMenu {
      resourcesMenu: sanityNavigation(
        navId: { current: { eq: "resources-menu" } }
      ) {
        items {
          _type
          link {
            link {
              _type
              external
              internal {
                ... on SanityPage {
                  id
                  _type
                  slug {
                    current
                  }
                }
                ... on SanityPost {
                  id
                  _type
                  slug {
                    current
                  }
                }
              }
            }
            text
          }
        }
        title
      }
      companyMenu: sanityNavigation(
        navId: { current: { eq: "company-menu" } }
      ) {
        items {
          _type
          link {
            link {
              _type
              external
              internal {
                ... on SanityPage {
                  id
                  _type
                  slug {
                    current
                  }
                }
                ... on SanityPost {
                  id
                  _type
                  slug {
                    current
                  }
                }
              }
            }
            text
          }
        }
        title
      }
      socialMenu: sanityNavigation(navId: { current: { eq: "social" } }) {
        items {
          link {
            _rawLink
          }
        }
      }
      sanitySiteSettings {
        companyDetails {
          address1
          address2
          county
          email
          phone
          postcode
        }
        awardsWon {
          image {
            asset {
              gatsbyImageData(height: 73)
            }
          }
          url
        }
        newsletterSetup {
          image {
            asset {
              gatsbyImageData(height: 566)
            }
          }
          text
          title
        }
      }
    }
  `)

  const definedAwardsWon =
    (previewData && previewData?.awardsWon) ||
    data.sanitySiteSettings?.awardsWon

  const renderLink = menuItem => {
    return (
      <>
        {menuItem?.link?.link?.internal ? (
          <GatsbyButton
            size="large"
            color="background"
            variant="text"
            to={prefixLink(menuItem)}
            sx={{
              ml: -4,
              fontWeight: "400 !important",
              width: "max-content !important",
            }}
          >
            {menuItem.link.text}
          </GatsbyButton>
        ) : menuItem?.link?.link?.external ? (
          <Link
            size="large"
            color="background.main"
            href={menuItem?.link?.link?.external}
            sx={{
              ml: -4,
              fontWeight: "400 !important",
              textDecoration: "unset",
              width: "max-content !important",
            }}
          >
            {menuItem.link.text}
          </Link>
        ) : null}
      </>
    )
  }

  const address = [
    data.sanitySiteSettings?.companyDetails?.address1,
    data.sanitySiteSettings?.companyDetails?.address2,
    data.sanitySiteSettings?.companyDetails?.county,
    data.sanitySiteSettings?.companyDetails?.postcode,
  ]
  return (
    <>
      {showNewsletter && data.sanitySiteSetting && (
        <Newsletter
          newsletterSetup={data.sanitySiteSettings?.newsletterSetup}
        />
      )}
      <Box sx={{ backgroundColor: "text.primary", py: { xs: 11, md: 14 } }}>
        <Container maxWidth="xl">
          <Divider sx={{ borderColor: "background.main" }} />
          <Grid
            container
            rowSpacing={6}
            sx={{ pt: { xs: 8, md: 11 }, pb: { xs: 8, md: 11 } }}
          >
            <Grid item xs={12} sm={5}>
              <Typography variant="h5" color="white.main" sx={{ mb: 5 }}>
                Contact
              </Typography>
              {data?.sanitySiteSettings?.companyDetails && (
                <List dense={true}>
                  {address.map((node, i) => {
                    return (
                      <ListItem key={node} sx={{ pl: 0, ml: 0, py: 0 }}>
                        <ListItemText
                          key={`address-item${i}`}
                          primary={node}
                          primaryTypographyProps={{
                            color: "background.main",
                            variant: "body1",
                          }}
                        />
                      </ListItem>
                    )
                  })}

                  <ListItem sx={{ pl: 0, ml: 0, py: 0, mt: 3 }}>
                    <ListItemText
                      primary={
                        <a
                          style={{ color: "inherit", textDecoration: "none" }}
                          href={`tel:${data.sanitySiteSettings?.companyDetails?.phone}`}
                        >
                          {data.sanitySiteSettings?.companyDetails?.phone}
                        </a>
                      }
                      primaryTypographyProps={{
                        color: "background.main",
                        variant: "body1",
                      }}
                    />
                  </ListItem>
                  <ListItem sx={{ pl: 0, ml: 0, py: 0 }}>
                    <ListItemText
                      primary={
                        <a
                          style={{ color: "inherit", textDecoration: "none" }}
                          href={`mailto:${data.sanitySiteSettings?.companyDetails?.email}`}
                        >
                          {data.sanitySiteSettings?.companyDetails?.email}
                        </a>
                      }
                      primaryTypographyProps={{
                        color: "background.main",
                        variant: "body1",
                      }}
                    />
                  </ListItem>
                </List>
              )}
            </Grid>

            <Divider
              sx={{
                width: "100%",
                my: 8,
                display: { xs: "flex", sm: "none" },
                borderColor: "background.main",
              }}
            />

            <Grid item xs={12} sm={3}>
              <Typography variant="h5" color="white.main" sx={{ mb: 5 }}>
                {data.resourcesMenu?.title}
              </Typography>
              <List dense={true} sx={{ pt: 0 }}>
                {data?.resourcesMenu?.items &&
                  data.resourcesMenu.items.map((menuItem, i) => {
                    return (
                      <ListItem
                        key={`footer-menu-item-${i}`}
                        sx={{ pl: 0, ml: 0 }}
                      >
                        {renderLink(menuItem)}
                      </ListItem>
                    )
                  })}
              </List>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="h5" color="white.main" sx={{ mb: 5 }}>
                {data.companyMenu?.title}
              </Typography>
              <List dense={true} sx={{ pt: 0 }}>
                {data?.companyMenu?.items &&
                  data.companyMenu.items.map((menuItem, i) => {
                    return (
                      <ListItem
                        key={`footer-menu-item-${i}`}
                        sx={{ pl: 0, ml: 0 }}
                      >
                        {renderLink(menuItem)}
                      </ListItem>
                    )
                  })}
              </List>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: "background.main" }} />

          <Grid container sx={{ py: { xs: 6, sm: 8 } }}>
            <Grid item xs={12} sm={5} sx={{ mb: { xs: 6, md: 0 } }}>
              <svg
                width="185"
                height="45"
                viewBox="0 0 185 45"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_28_5070)">
                  <g opacity="0.82">
                    <path
                      d="M19.3548 34.2461C18.6359 33.5601 17.6682 33.2389 16.4931 33.2893C15.0392 33.3536 13.5599 34.1107 12.8134 35.1753C12.3249 35.8728 12.1935 36.6392 12.4332 37.3917C12.659 38.0984 13.1659 38.2843 13.5253 38.2269C13.9263 38.165 14.3295 37.791 14.3295 37.0981C14.3295 36.0449 15.1912 35.1868 16.2488 35.1868C17.3687 35.1868 18.4009 35.3382 18.7419 37.1623C18.947 38.2682 18.53 39.934 17.3525 40.8448C16.1359 41.7855 14.3364 41.7488 12.1475 40.7416C11.0783 40.2483 10.4101 39.5141 10.1659 38.555C9.82719 37.238 10.3779 35.7053 11.0714 34.7692L11.3687 34.3677C12.0476 33.4515 12.6336 32.7211 13.1267 32.1765C13.4631 31.8048 13.9516 31.2657 14.7535 30.3571C17.0346 27.7759 18.1152 25.7246 18.1659 23.7973C18.1659 23.7905 18.1705 23.7836 18.1705 23.7767C18.1705 23.7721 18.1682 23.7675 18.1682 23.7629C18.1797 23.2513 18.1198 22.7488 17.9885 22.2463C17.4147 20.0689 14.5714 17.4074 12.1567 15.3309C10.5737 13.9681 10.8203 12.9035 11.1728 12.2794C11.1728 12.2794 11.1728 12.2794 11.1728 12.2771C11.1728 12.2771 11.1728 12.2771 11.1728 12.2748C11.2673 12.1073 11.3687 11.9696 11.4447 11.871C11.47 11.8365 11.4908 11.809 11.5069 11.7861C11.7028 11.5084 11.8963 11.3157 12.0161 11.2079C12.2327 11.5268 12.8018 12.2885 13.6659 12.9035C14.318 13.3669 14.6751 13.5734 15.0207 13.773C15.2558 13.9084 15.4793 14.0369 15.788 14.2434C16.3111 14.5898 16.235 14.4086 16.7074 15.1657C16.9055 15.4847 17.0783 15.76 17.2235 15.9573C17.2995 16.0606 17.3687 16.1592 17.4332 16.2487C17.7972 16.7604 18.0392 17.0999 18.5115 17.0999C18.5806 17.0999 18.6452 17.0999 18.7051 17.1045C19.0576 17.1183 19.424 17.1298 19.5899 16.5791C19.6267 16.4552 19.6728 16.3588 19.7189 16.2671C19.8733 15.9436 19.9401 15.7141 19.5622 15.2552C19.4516 15.1199 19.3226 15.0051 19.1843 14.8812C18.712 14.4591 18.5691 14.6793 18.1797 12.9264C17.6705 10.6503 17.5829 6.99533 17.5806 6.95633C17.5737 6.86914 17.5046 6.80719 17.4147 6.81407C17.3295 6.82096 17.2673 6.89438 17.2719 6.97927C17.2719 6.99304 17.2788 7.1743 17.1359 7.27066C16.9816 7.37162 16.5461 7.45192 15.3825 6.83243C14.7212 6.47909 14.235 6.17164 13.8065 5.9009C13.6982 5.83206 13.5945 5.76782 13.4931 5.70358C13.3871 5.52461 12.8134 4.48065 13.0438 3.56059C13.1982 2.9411 13.8756 2.37897 14.371 2.04169C14.4931 2.161 14.659 2.23442 14.8433 2.23442C15.2143 2.23442 15.5138 1.93615 15.5138 1.56675C15.5138 1.19734 15.2143 0.899071 14.8433 0.899071C14.4723 0.899071 14.1728 1.19734 14.1728 1.56675C14.1728 1.5851 14.1774 1.60346 14.1774 1.62181C12.9263 1.94073 12.0829 1.57363 11.6129 1.23406C11.6705 1.1354 11.7074 1.02067 11.7074 0.899071C11.7074 0.52967 11.4078 0.231396 11.0369 0.231396C10.6659 0.231396 10.3664 0.52967 10.3664 0.899071C10.3664 1.26847 10.6659 1.56675 11.0369 1.56675C11.0622 1.56675 11.0853 1.56216 11.1106 1.55986C11.2742 2.02104 11.5023 2.67265 11.7028 3.27838C12.0207 4.24203 11.2811 4.7468 11.2488 4.76745C11.2281 4.78122 11.2143 4.79958 11.2028 4.82023C11.023 4.79499 10.8318 4.77434 10.6221 4.75828C9.63134 4.68715 8.7212 4.85235 7.947 5.21257C7.9977 5.1185 8.02995 5.01066 8.02995 4.89594C8.02995 4.52654 7.73041 4.22827 7.35945 4.22827C6.98848 4.22827 6.68894 4.52654 6.68894 4.89594C6.68894 5.25387 6.97235 5.54526 7.32949 5.56132C6.53917 6.09363 5.94009 6.86914 5.6129 7.84426C5.58295 7.93375 5.5553 8.06912 5.52995 8.2412C5.41014 8.05765 5.20276 7.93834 4.96774 7.93834C4.59677 7.93834 4.29723 8.23661 4.29723 8.60601C4.29723 8.97541 4.59677 9.27369 4.96774 9.27369C5.15438 9.27369 5.32258 9.19797 5.4424 9.07637C5.36406 10.1272 5.32028 11.7218 5.29723 13.6331C5.17511 13.4725 4.98387 13.3669 4.76498 13.3669C4.39401 13.3669 4.09447 13.6652 4.09447 14.0346C4.09447 14.404 4.39401 14.7023 4.76498 14.7023C4.97926 14.7023 5.1659 14.6013 5.28802 14.4476C5.2765 15.8472 5.2765 17.389 5.28571 18.9928L3.93318 18.5752C3.9447 18.5294 3.94931 18.4812 3.94931 18.4307C3.94931 18.0613 3.64977 17.763 3.2788 17.763C2.90783 17.763 2.60829 18.0613 2.60829 18.4307C2.60829 18.8001 2.90783 19.0984 3.2788 19.0984C3.48617 19.0984 3.67051 19.002 3.79263 18.8552L5.28571 19.3163C5.29032 20.0964 5.29493 20.888 5.30415 21.6865L2.67972 20.8628C2.67512 20.498 2.37788 20.2043 2.00922 20.2043C1.64055 20.2043 1.33871 20.5026 1.33871 20.872C1.33871 21.2414 1.63825 21.5396 2.00922 21.5396C2.27419 21.5396 2.5 21.3859 2.6106 21.1633L5.30876 22.01C5.31336 22.5469 5.32028 23.0838 5.32719 23.6207H1.32258C1.25346 23.327 0.988479 23.1067 0.670507 23.1067C0.299539 23.1067 0 23.405 0 23.7744C0 24.1438 0.299539 24.4421 0.670507 24.4421C0.988479 24.4421 1.25115 24.2218 1.32258 23.9281H5.3318C5.34332 24.7954 5.35484 25.6581 5.36636 26.5047L2.64977 26.8673C2.64286 26.8443 2.63825 26.8214 2.62903 26.8007C2.52765 26.5598 2.28802 26.3923 2.01152 26.3923C1.64055 26.3923 1.34101 26.6906 1.34101 27.06C1.34101 27.4294 1.64055 27.7277 2.01152 27.7277C2.34332 27.7277 2.61751 27.489 2.67051 27.1747L5.37097 26.8145C5.38249 27.5877 5.39631 28.3472 5.40783 29.0837L3.85023 29.593C3.73272 29.4026 3.52304 29.2741 3.28111 29.2741C2.91014 29.2741 2.6106 29.5724 2.6106 29.9418C2.6106 30.3112 2.91014 30.6094 3.28111 30.6094C3.65207 30.6094 3.95161 30.3112 3.95161 29.9418C3.95161 29.9234 3.947 29.9051 3.947 29.8867L5.41475 29.4072C5.5 34.2048 5.59447 38.0089 5.60599 38.4242C5.60599 38.4862 5.61982 39.9385 6.8341 41.2532C8.08295 42.6069 9.18203 43.4077 11.4032 43.7679C11.5392 43.8 13.318 44.2199 14.1452 44.2199C17.7673 44.2199 20.712 41.2854 20.712 37.6808C20.712 37.5937 20.6959 35.5195 19.3525 34.2392L19.3548 34.2461ZM14.5253 30.1529C14.2189 30.4993 13.9608 30.7907 13.7373 31.0408C13.4447 30.6714 12.6083 29.733 11.2811 29.1043C11.5945 28.7097 11.9447 28.2852 12.3318 27.8332C13.4263 26.5598 13.6037 25.2313 13.3203 23.9304H17.8525C17.7512 25.743 16.6935 27.6955 14.523 30.1529H14.5253ZM17.6935 22.3243C17.8064 22.7534 17.8641 23.1847 17.8664 23.623H13.2465C12.8502 22.1706 11.9194 20.7618 11.0899 19.5091C11.0115 19.3898 10.9355 19.275 10.8594 19.1603C12.3318 18.7359 13.6198 18.1278 14.1567 17.5496C15.7857 19.1167 17.3111 20.872 17.6935 22.3243ZM11.9585 15.565C12.5783 16.0973 13.2581 16.7007 13.9309 17.3363C13.4424 17.8732 12.1382 18.4697 10.6797 18.8827C10.6198 18.7886 10.5576 18.6946 10.5 18.6051C10.3502 18.3665 10.2051 18.1393 10.0645 17.9213C9.16359 16.5126 8.50461 15.4824 8.29954 14.1883H10.841C11.0576 14.6495 11.4263 15.1084 11.9562 15.565H11.9585ZM8.34101 12.0798C8.39401 11.7517 8.48848 11.4534 8.60599 11.1849L10.8433 12.284C10.5968 12.8163 10.553 13.3509 10.7212 13.8809H8.26267C8.20968 13.344 8.22811 12.7589 8.34101 12.0798ZM8.03456 12.0293C7.92166 12.7222 7.90092 13.3256 7.95392 13.8786H5.64286C5.63134 13.8786 5.61982 13.8832 5.60829 13.8855C5.62903 11.8067 5.67511 10.0928 5.75806 9.0213L8.33871 11.0197C8.20276 11.3226 8.09677 11.6553 8.03456 12.0293ZM5.60369 14.1814C5.61521 14.1837 5.62673 14.1883 5.64055 14.1883H7.98848C8.19816 15.5787 8.89401 16.6686 9.80415 18.0888C9.9424 18.3068 10.0876 18.5317 10.2373 18.7703C10.2788 18.8345 10.3226 18.9033 10.3664 18.9699C9.6682 19.1534 8.947 19.2911 8.2765 19.3576C7.08986 19.4747 5.95392 19.1901 5.60138 19.0892C5.59217 17.3454 5.59217 15.6751 5.60369 14.1814ZM11.2604 11.6071C11.2442 11.6278 11.2258 11.6553 11.2028 11.6851C11.1198 11.793 11.0507 11.9031 10.9862 12.0109L8.74424 10.9096C9.14746 10.1731 9.75346 9.67291 10.318 9.34252L11.5599 11.0794C11.5806 11.1092 11.6106 11.1276 11.6429 11.139C11.5276 11.2584 11.3963 11.4121 11.2604 11.6048V11.6071ZM11.6521 10.5792L10.6429 9.16814C11.0599 8.96165 11.4217 8.84463 11.606 8.79415C11.4585 9.3494 11.4147 10.001 11.6498 10.5792H11.6521ZM17.3088 7.52534C17.3088 7.52534 17.3157 7.52075 17.3203 7.51846C17.424 8.70697 17.4816 11.1964 17.8825 12.9929C18.2949 14.8422 18.5069 14.6816 18.9839 15.1084C19.1175 15.2277 19.2327 15.3309 19.3295 15.448C19.6106 15.7898 19.5668 15.8839 19.447 16.1317C19.4009 16.2304 19.3456 16.3428 19.3018 16.4873C19.2051 16.8085 19.0737 16.804 18.7258 16.7925C18.6613 16.7925 18.5922 16.7879 18.5207 16.7879C18.2212 16.7879 18.0507 16.5676 17.6959 16.0674C17.6313 15.9757 17.5599 15.877 17.4839 15.7715C17.3456 15.5833 17.1774 15.3126 16.9816 15.0005C16.4908 14.2159 16.5484 14.3673 15.97 13.9841C15.6521 13.773 15.4171 13.6377 15.1866 13.5046C14.8479 13.3096 14.4977 13.1054 13.8571 12.6511C12.8134 11.9077 12.2028 10.9073 12.1959 10.8981C12.1959 10.8958 12.1912 10.8935 12.1889 10.8912C12.1889 10.889 12.1866 10.8844 12.1843 10.8821C11.3894 9.88629 12.0092 8.30085 12.3894 7.83738C12.9147 7.19724 14.106 6.73377 14.3986 6.62822C14.6498 6.77736 14.924 6.93568 15.2419 7.10546C16.2373 7.63547 16.9332 7.77773 17.3111 7.52993L17.3088 7.52534ZM12 3.17972C11.7995 2.5717 11.5714 1.91779 11.4055 1.45661C11.4055 1.45661 11.4078 1.45661 11.4101 1.45432C11.8894 1.80536 12.7189 2.19542 13.9055 1.9981C13.424 2.3675 12.8963 2.89292 12.7465 3.48488C12.5691 4.19385 12.7995 4.93954 13.0046 5.41219C12.5829 5.18045 12.1544 5.00148 11.5876 4.88447C11.8479 4.63667 12.288 4.05619 12 3.17972ZM10.6037 5.06343C12.0829 5.16898 12.6705 5.54067 13.6452 6.16016C13.7765 6.24276 13.9171 6.33225 14.0645 6.42173C13.5806 6.61675 12.6336 7.0504 12.1475 7.64006C12.0069 7.80985 11.8433 8.10353 11.7143 8.46146C11.7028 8.46146 11.6935 8.46146 11.682 8.46146C11.6682 8.46376 11.1267 8.57848 10.4631 8.91576L8.02304 5.50396C8.76037 5.15292 9.63364 4.9946 10.6037 5.06343ZM7.69816 5.67604L10.1406 9.09013C9.54608 9.44118 8.91014 9.96889 8.48157 10.7398L5.79032 8.65649C5.82488 8.31921 5.86405 8.07371 5.91014 7.93834C6.24654 6.93568 6.86866 6.16705 7.69816 5.67375V5.67604ZM5.60369 19.4104C5.97005 19.5091 6.76958 19.6926 7.68894 19.6926C7.8894 19.6926 8.09908 19.6834 8.30645 19.6628C9.04147 19.5917 9.81106 19.4425 10.5461 19.2475C10.6382 19.3898 10.7327 19.532 10.8295 19.6789C11.6267 20.8834 12.5207 22.2371 12.9147 23.623H5.64055C5.62442 22.2073 5.6106 20.7871 5.60138 19.4104H5.60369ZM12.9954 23.9327C13.2788 25.1763 13.1244 26.4405 12.0968 27.6336C11.6843 28.1131 11.318 28.5605 10.9908 28.9758C9.34562 28.3403 6.34101 28.8818 5.7212 29.0034C5.69355 27.3858 5.6682 25.6673 5.64516 23.9327H12.9954ZM7.06221 41.0513C5.93318 39.8284 5.91475 38.4449 5.91475 38.4265C5.91244 38.321 5.81336 34.3585 5.7235 29.3154C6.15438 29.2305 9.1659 28.6661 10.788 29.2305C9.56682 30.8068 8.93087 31.892 8.65207 32.3693C8.58986 32.4748 8.54839 32.5482 8.52304 32.5849C6.98848 34.8036 6.51613 36.8526 6.94931 39.4338C7.24194 41.1867 8.51843 42.3133 9.62903 42.9763C8.59677 42.5496 7.86175 41.9163 7.06221 41.0513ZM14.1452 43.9193C13.3571 43.9193 11.5691 43.4972 11.4747 43.4765C11.3456 43.4375 7.74885 42.327 7.25576 39.3856C6.83871 36.8893 7.29493 34.9092 8.7788 32.7639C8.80645 32.7249 8.85253 32.6446 8.92166 32.5299C9.20276 32.0503 9.84562 30.9536 11.0876 29.359C12.4424 29.9831 13.288 30.9743 13.5276 31.2817C13.2696 31.5685 13.0668 31.7934 12.9009 31.9769C12.4002 32.5291 11.8072 33.2671 11.1221 34.191L10.8249 34.5926C10.1406 35.5149 9.48157 37.1256 9.86866 38.6376C10.1382 39.6885 10.8617 40.4915 12.0207 41.0261C14.9263 42.366 16.629 41.8039 17.5461 41.0949C18.8203 40.1083 19.2719 38.3072 19.0484 37.1118C18.6659 35.056 17.4032 34.8839 16.2512 34.8839C15.023 34.8839 14.0207 35.8797 14.0207 37.1049C14.0207 37.6166 13.7488 37.8873 13.4793 37.9309C13.1567 37.9791 12.8664 37.7359 12.7281 37.3068C12.5184 36.6506 12.6336 35.9784 13.0668 35.3612C13.7512 34.3861 15.1636 33.6633 16.5046 33.606C17.6014 33.5578 18.4816 33.8515 19.1382 34.4755C20.3848 35.664 20.4032 37.674 20.4032 37.6946C20.4032 41.1293 17.5968 43.9262 14.1452 43.9262V43.9193Z"
                      fill="white"
                    />
                  </g>
                  <path
                    d="M16.2996 10.8385C16.5744 10.8385 16.7973 10.6166 16.7973 10.3429C16.7973 10.0692 16.5744 9.84729 16.2996 9.84729C16.0247 9.84729 15.8019 10.0692 15.8019 10.3429C15.8019 10.6166 16.0247 10.8385 16.2996 10.8385Z"
                    fill="white"
                  />
                  <path
                    d="M29.6083 8.97771H30.1981V8.50506H33.1382V20.3809H32.6636V20.9683H34.2534V20.3809H33.7788V8.50506H36.7189V8.97771H37.3064V7.91998H29.6083V8.97771Z"
                    fill="white"
                  />
                  <path
                    d="M49.4793 8.50506H49.9539V14.108H42.8018V8.50506H43.2742V7.91998H41.6843V8.50506H42.159V20.3809H41.6843V20.9683H43.2742V20.3809H42.8018V14.6931H49.9539V20.3809H49.4793V20.9683H51.0691V20.3809H50.5945V8.50506H51.0691V7.91998H49.4793V8.50506Z"
                    fill="white"
                  />
                  <path
                    d="M56.2189 8.50506H56.6935V20.3809H56.2189V20.9683H63.9355V19.9106H63.3456V20.3809H57.3364V14.6931H63.3456V15.1657H63.9355V13.6354H63.3456V14.108H57.3364V8.50506H63.3456V8.97771H63.9355V7.91998H56.2189V8.50506Z"
                    fill="white"
                  />
                  <path
                    d="M84 14.8537C83.3203 14.3466 82.5484 14.1493 82.1336 14.0415C82.0783 14.0277 82.03 14.0162 81.9885 14.0048C80.023 13.5069 78.523 12.9723 78.5392 11.0358C78.5392 9.43888 79.7627 8.27791 81.447 8.27791C82.9977 8.27791 83.9424 9.11537 84.2512 10.7651L84.2604 10.8109H84.9009L84.8917 10.7467C84.5922 8.49358 82.9332 7.69054 81.4816 7.69054C80.4263 7.69054 79.4424 8.076 78.7834 8.74826C78.1797 9.36546 77.871 10.1869 77.894 11.123C77.894 13.601 80.212 14.17 81.3249 14.443L81.4355 14.4728C82.7028 14.8078 84.6175 15.3172 84.6175 17.5909C84.6175 19.4563 82.9516 20.6104 81.4101 20.6104C78.712 20.6104 78.2949 17.942 78.2558 17.6368L78.2488 17.5864H77.6106L77.6152 17.6483C77.8018 20.2525 79.9977 21.1978 81.4286 21.1978C82.3963 21.1978 83.3617 20.8238 84.0783 20.1745C84.841 19.4815 85.2604 18.5408 85.2604 17.5244C85.2604 16.3795 84.8364 15.4824 83.9977 14.856L84 14.8537Z"
                    fill="white"
                  />
                  <path
                    d="M90.0437 8.50506H90.5184V20.3809H90.0437V20.9683H97.7603V19.9106H97.1727V20.3809H91.1612V14.6931H97.1727V15.1657H97.7603V13.6354H97.1727V14.108H91.1612V8.50506H97.1727V8.97771H97.7603V7.91998H90.0437V8.50506Z"
                    fill="white"
                  />
                  <path
                    d="M108.763 7.9544L108.749 7.91998H107.988L102.618 20.3832H102.018V20.9706H103.062L105.012 16.4621H111.728L113.661 20.9362L113.675 20.9706H114.719V20.3832H114.101L108.763 7.9544ZM111.449 15.8724H105.286L108.359 8.61289L111.449 15.8724Z"
                    fill="white"
                  />
                  <path
                    d="M126.521 8.50506H126.995V14.108H119.843V8.50506H120.318V7.91998H118.728V8.50506H119.2V20.3809H118.728V20.9683H120.318V20.3809H119.843V14.6931H126.995V20.3809H126.521V20.9683H128.111V20.3809H127.636V8.50506H128.111V7.91998H126.521V8.50506Z"
                    fill="white"
                  />
                  <path
                    d="M139.901 7.69283C138.067 7.69283 136.359 8.36969 135.094 9.59949C133.809 10.8499 133.101 12.5639 133.101 14.4269V14.4453C133.101 16.3175 133.809 18.0361 135.094 19.2888C136.357 20.5186 138.064 21.1978 139.901 21.1978C141.737 21.1978 143.445 20.5209 144.707 19.2888C145.993 18.0361 146.7 16.3175 146.7 14.4453C146.7 12.5731 145.993 10.8522 144.707 9.60179C143.445 8.37198 141.737 7.69283 139.901 7.69283ZM146.058 14.4453C146.058 17.903 143.352 20.6104 139.901 20.6104C136.449 20.6104 133.744 17.9099 133.744 14.4637V14.4453C133.744 10.9876 136.449 8.2802 139.901 8.2802C143.352 8.2802 146.058 10.9876 146.058 14.4453Z"
                    fill="white"
                  />
                  <path
                    d="M159.59 14.6197C160.154 13.8809 160.468 12.8874 160.426 11.9627C160.426 10.4874 159.622 9.01901 158.521 8.47064C157.922 8.12189 157.159 7.91998 155.035 7.91998H151.537V8.50735H152.012V20.3832H151.537V20.9706H152.652V8.50735H155.035C156.588 8.50735 157.502 8.5303 158.267 9.0213C159.189 9.61785 159.786 10.7788 159.786 11.9811C159.786 13.0824 159.449 13.9199 158.696 14.7023C157.887 15.5076 156.343 15.5076 155.419 15.5076H154.94L158.813 20.9477L158.832 20.9729H160.06V20.3855H159.207L156.145 16.0927C157.512 16.0376 158.638 15.8747 159.592 14.6243L159.59 14.6197Z"
                    fill="white"
                  />
                  <path
                    d="M171.24 14.8537C170.56 14.3466 169.788 14.1493 169.373 14.0415C169.318 14.0277 169.27 14.0162 169.228 14.0048C167.263 13.5069 165.763 12.9723 165.779 11.0358C165.779 9.43888 167.002 8.27791 168.687 8.27791C170.237 8.27791 171.182 9.11537 171.491 10.7651L171.5 10.8109H172.141L172.131 10.7467C171.832 8.49358 170.173 7.69054 168.721 7.69054C167.666 7.69054 166.682 8.076 166.023 8.74826C165.419 9.36546 165.111 10.1869 165.134 11.123C165.134 13.601 167.452 14.17 168.564 14.443L168.675 14.4728C169.942 14.8078 171.857 15.3172 171.857 17.5909C171.857 19.4563 170.191 20.6104 168.65 20.6104C165.952 20.6104 165.535 17.942 165.495 17.6368L165.488 17.5864H164.848L164.853 17.6483C165.039 20.2525 167.235 21.1978 168.666 21.1978C169.634 21.1978 170.599 20.8238 171.316 20.1745C172.078 19.4815 172.498 18.5408 172.498 17.5244C172.498 16.3795 172.074 15.4824 171.235 14.856L171.24 14.8537Z"
                    fill="white"
                  />
                  <path
                    d="M185 8.97771V7.91998H177.283V8.50506H177.758V20.3809H177.283V20.9683H185V19.9106H184.41V20.3809H178.401V14.6931H184.41V15.1657H185V13.6354H184.41V14.108H178.401V8.50506H184.41V8.97771H185Z"
                    fill="white"
                  />
                  <path
                    d="M51.235 36.6208V29.6848H53.288C54.0069 29.6848 54.5322 29.7559 54.9262 29.8959C55.6129 30.1483 56.0391 30.8114 56.0391 31.7177C56.0391 32.7547 55.6036 33.4385 54.705 33.7299C54.2995 33.8606 53.7534 33.9318 53.2695 33.9318H52.0069V36.6185H51.2373L51.235 36.6208ZM53.2258 33.248C53.811 33.248 54.3179 33.1563 54.5691 33.0369C54.993 32.835 55.2465 32.4037 55.2465 31.7383C55.2465 31.2358 55.0253 30.7724 54.5691 30.5704C54.2557 30.419 53.7096 30.3685 53.2557 30.3685H52.0023V33.248H53.2258Z"
                    fill="white"
                  />
                  <path
                    d="M59.3801 36.6208V29.6848H63.4953V30.3892H60.1497V32.6744H63.3041V33.3582H60.1497V35.9256H63.6566V36.6208H59.3801Z"
                    fill="white"
                  />
                  <path
                    d="M67.0483 36.6208V29.6848H67.8364C70.8387 33.8721 71.1728 34.3562 71.7096 35.1822C71.6797 33.9937 71.6682 32.897 71.6682 29.6848H72.4262V36.6208H71.7995C68.7373 32.2316 68.2603 31.5272 67.7765 30.8022C67.8064 32.2729 67.8064 33.248 67.8064 36.6208H67.0483Z"
                    fill="white"
                  />
                  <path
                    d="M77.9216 36.6208V30.3892H75.6774V29.6848H81.0161V30.3892H78.6912V36.6208H77.9239H77.9216Z"
                    fill="white"
                  />
                  <path
                    d="M84.1151 36.6208V29.6848H88.2303V30.3892H84.8847V32.6744H88.0391V33.3582H84.8847V35.9256H88.3916V36.6208H84.1151Z"
                    fill="white"
                  />
                  <path
                    d="M92.917 36.6208L90.9055 29.6848H91.7557C92.8779 33.6518 93.0898 34.4274 93.3318 35.4231C93.6037 34.4572 93.8364 33.6908 95.182 29.6848H95.8687C97.1935 33.7413 97.4147 34.4457 97.6981 35.4231C97.9309 34.487 98.1221 33.7413 99.2949 29.6848H100.134L98.0415 36.6208H97.3341C96 32.484 95.788 31.8186 95.4931 30.793C95.2004 31.7796 94.9769 32.4748 93.6221 36.6208H92.9147H92.917Z"
                    fill="white"
                  />
                  <path
                    d="M102.447 36.6208L105.297 29.6848H106.005L108.804 36.6208H107.954L107.136 34.5168H104.104L103.265 36.6208H102.447ZM106.876 33.8423C105.995 31.6167 105.834 31.1647 105.643 30.6324C105.452 31.1555 105.27 31.619 104.369 33.8423H106.876Z"
                    fill="white"
                  />
                  <path
                    d="M111.853 36.6208V29.6848H112.641C115.643 33.8721 115.977 34.3562 116.514 35.1822C116.484 33.9937 116.472 32.897 116.472 29.6848H117.23V36.6208H116.604C113.541 32.2316 113.065 31.5272 112.581 30.8022C112.611 32.2729 112.611 33.248 112.611 36.6208H111.853Z"
                    fill="white"
                  />
                  <path
                    d="M125 35.0904H125.041C125.647 35.7237 126.588 36.0472 127.417 36.0472C128.611 36.0472 129.136 35.6044 129.136 34.8197C129.136 34.2163 128.671 33.9341 127.59 33.5922L126.62 33.2802C125.569 32.9475 125.113 32.3234 125.113 31.5089C125.113 30.3502 126.053 29.5357 127.65 29.5357C128.336 29.5357 129.256 29.7881 129.601 30.1093V30.9559H129.56C129.055 30.4626 128.205 30.2309 127.528 30.2309C126.253 30.2309 125.889 30.8458 125.889 31.44C125.889 31.9632 126.182 32.3349 127.143 32.6377L128.224 32.9796C129.355 33.3421 129.912 33.8148 129.912 34.7509C129.912 35.8086 129.214 36.7631 127.376 36.7631C126.528 36.7631 125.546 36.49 125 35.9876V35.0927V35.0904Z"
                    fill="white"
                  />
                  <path
                    d="M132.274 36.6208L135.124 29.6848H135.832L138.631 36.6208H137.781L136.963 34.5168H133.931L133.092 36.6208H132.274ZM136.703 33.8423C135.823 31.6167 135.661 31.1647 135.47 30.6324C135.279 31.1555 135.097 31.619 134.196 33.8423H136.703Z"
                    fill="white"
                  />
                  <path
                    d="M141.682 36.6208V29.6848H142.47C145.472 33.8721 145.806 34.3562 146.343 35.1822C146.313 33.9937 146.302 32.897 146.302 29.6848H147.06V36.6208H146.433C143.371 32.2316 142.894 31.5272 142.41 30.8022C142.44 32.2729 142.44 33.248 142.44 36.6208H141.682Z"
                    fill="white"
                  />
                  <path
                    d="M151.039 29.6848H152.91C153.76 29.6848 154.387 29.7858 154.841 29.9165C156.115 30.2791 156.984 31.2657 156.984 33.0668C156.984 34.6981 156.286 35.804 154.88 36.3088C154.325 36.5107 153.627 36.6208 152.657 36.6208H151.039V29.6848ZM152.809 35.9256C153.749 35.9256 154.518 35.7352 154.942 35.5034C155.742 35.0813 156.196 34.2851 156.196 33.0874C156.196 32.0412 155.74 31.0431 154.719 30.6622C154.334 30.5108 153.82 30.3915 152.97 30.3915H151.788V35.9279H152.809V35.9256Z"
                    fill="white"
                  />
                  <path
                    d="M159.963 35.0904H160.005C160.611 35.7237 161.551 36.0472 162.38 36.0472C163.574 36.0472 164.099 35.6044 164.099 34.8197C164.099 34.2163 163.634 33.9341 162.553 33.5922L161.583 33.2802C160.532 32.9475 160.076 32.3234 160.076 31.5089C160.076 30.3502 161.016 29.5357 162.613 29.5357C163.3 29.5357 164.219 29.7881 164.565 30.1093V30.9559H164.523C164.018 30.4626 163.168 30.2309 162.491 30.2309C161.217 30.2309 160.853 30.8458 160.853 31.44C160.853 31.9632 161.145 32.3349 162.106 32.6377L163.187 32.9796C164.318 33.3421 164.876 33.8148 164.876 34.7509C164.876 35.8086 164.177 36.7631 162.339 36.7631C161.491 36.7631 160.509 36.49 159.963 35.9876V35.0927V35.0904Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_28_5070">
                    <rect
                      width="185"
                      height="44"
                      fill="white"
                      transform="translate(0 0.226807)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </Grid>

            <Grid item xs={12} md={7} sx={{ mb: { xs: 0, md: 0 } }}>
              <Divider
                sx={{
                  display: { xs: "block", md: "none" },
                  borderColor: "background.main",
                }}
              />
              {definedAwardsWon && definedAwardsWon.length !== 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: { xs: 7, md: 0 },
                  }}
                >
                  {definedAwardsWon.map((node, i) => {
                    return (
                      <Box sx={{ mx: { xs: 1, md: 0 } }}>
                        <GatsbyImage
                          key={`award-${i}`}
                          image={
                            getGatsbyImageData(
                              previewData?.image?.asset?._ref,
                              { maxWidth: 80 },
                              sanityConfig,
                            ) || getImage(node.image?.asset)
                          }
                          layout="constrained"
                          alt={node.image?.asset?.altText}
                        />
                      </Box>
                    )
                  })}
                </Box>
              )}
            </Grid>
          </Grid>

          <Divider
            sx={{
              display: { xs: "none", md: "block" },
              borderColor: "background.main",
            }}
          />

          <Grid container sx={{ pt: { xs: 6, sm: 8 }, pb: { xs: 0, sm: 8 } }}>
            <Grid
              item
              xs={12}
              sm={6}
              md={5}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: { xs: 2, md: 0 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography color="background.main" variant="caption">
                  {`© ${new Date().getFullYear()}  Seahorse Restaurant  |  Designed & Developed by `}{" "}
                  <Link
                    color="background.main"
                    className="link-animation"
                    href="https://www.gendall.co.uk"
                  >
                    Gendall
                  </Link>
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={7}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: "flex-start",
                    sm: "flex-end",
                    md: "flex-end",
                  },
                }}
              >
                {data?.socialMenu?.items &&
                  data.socialMenu.items.map((node, i) => {
                    return (
                      <Box
                        key={`social-${i}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: { xs: "flex-start", md: "flex-end" },
                          "&:hover": {
                            "& svg .social-svg-icon": {
                              fill: "#C28C43 !important",
                            },
                          },
                        }}
                      >
                        <SocialIcon
                          network={
                            node.link._rawLink.external.includes("twitter")
                              ? "x"
                              : ""
                          }
                          style={{ height: 38, width: 38 }}
                          url={node.link._rawLink.external}
                          bgColor="transparent"
                          fgColor={theme.palette.background.main}
                        />
                      </Box>
                    )
                  })}
              </Box>
            </Grid>
          </Grid>
        </Container>

        <CookieBanner />
      </Box>
    </>
  )
}
